import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { ROUTES } from '../../../../../constants/RouteNameMapping';
import {
  useCreateSourcingList,
  useDeleteSourcingList,
  useRenameSourcingList,
} from '../../../../../pages/Sourcing/hooks/useSourcingListActions';
import { SourcingList } from '../../../../../schemas/SourcingList.schema';
import { useSourcingListsQuery } from '../../../../../services/queries-hooks/SourcingListsQueriesHooks';

export const SEARCH_THRESHOLD = 10;

export function useSourcingNavigation() {
  const navigate = useNavigate();
  const { data, isLoading } = useSourcingListsQuery();

  const lists = data?.lists ?? [];
  const savedSearches = data?.savedSearches ?? [];

  const [listsExpanded, setListsExpanded] = useState(true);
  const [savedSearchesExpanded, setSavedSearchesExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQuery = useDebouncedValue(searchQuery);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const deleteTargetRef = useRef<SourcingList | null>(null);

  const createList = useCreateSourcingList();
  const deleteList = useDeleteSourcingList();
  const renameList = useRenameSourcingList();

  const handleCreateList = (name: string, type: 'company' | 'person') => {
    createList.mutate(
      { name, entityType: type },
      {
        onSuccess: (newList) => {
          navigate(`/${ROUTES.SOURCING}/${ROUTES.LISTS}/${newList.id}`);
        },
        onSettled: () => {
          setCreateDialogOpen(false);
        },
      }
    );
  };

  const handleRenameList = (item: SourcingList, name: string) => {
    renameList.mutate({ id: item.id, name });
  };

  const handleDeleteListConfirm = () => {
    const target = deleteTargetRef.current;
    if (!target) {
      return;
    }
    deleteList.mutate(target.id, {
      onSettled: () => {
        setDeleteDialogOpen(false);
      },
    });
  };

  const filteredLists = debouncedQuery
    ? lists.filter((l) => l.name.toLowerCase().includes(debouncedQuery.toLowerCase()))
    : lists;

  const filteredSavedSearches = debouncedQuery
    ? savedSearches.filter((s) => s.name.toLowerCase().includes(debouncedQuery.toLowerCase()))
    : savedSearches;

  const showSearch = lists.length + savedSearches.length > SEARCH_THRESHOLD;

  return {
    loading: isLoading,
    filteredLists,
    filteredSavedSearches,
    showSearch,
    searchQuery,
    setSearchQuery,
    listsExpanded,
    setListsExpanded,
    savedSearchesExpanded,
    setSavedSearchesExpanded,
    createDialogOpen,
    setCreateDialogOpen,
    deleteDialogOpen,
    setDeleteDialogOpen,
    deleteTargetRef,
    handleCreateList,
    isCreating: createList.isPending,
    handleRenameList,
    handleDeleteListConfirm,
    isDeleting: deleteList.isPending,
  };
}

function useDebouncedValue<T>(value: T, delay = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = window.setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}
